import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
	getSelectVendorListHeight,
	isDesktopViewport,
} from "~/features/products/filters/sections/responsive-select-vendor-height.util";
import type { PharmacyPickerVendorSelectProps } from "~/features/products/pharmacy-picker/pharmacy-picker-vendor-select.component";
import { PharmacyPickerVendorSelect } from "~/features/products/pharmacy-picker/pharmacy-picker-vendor-select.component";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";

const SEARCH_SECTION_HEIGHT_PX =
	PharmacyPickerVendorSelect.SEARCH_SECTION_HEIGHT_PX;

const EXPANDED_VIEWPORT_CLASSES =
	"max-md:[&>[data-radix-scroll-area-viewport]]:!h-auto max-md:[&>[data-radix-scroll-area-viewport]]:!min-h-full [&>[data-radix-scroll-area-viewport]]:!overflow-y-visible max-md:[&>[data-radix-scroll-area-viewport]]:!overflow-x-visible md:[&>[data-radix-scroll-area-viewport]]:!h-full md:[&>[data-radix-scroll-area-viewport]]:!overflow-y-scroll";

type PharmacyPickerScrollLayout = {
	className: string;
	height: string | undefined;
};

function getViewportInnerHeight(): number {
	if (typeof window === "undefined") {
		return 0;
	}
	return window.innerHeight;
}

function getSearchSectionHeightPx(
	enablePharmacyFilters: boolean | undefined,
): number {
	if (enablePharmacyFilters) {
		return PharmacyPickerVendorSelect.SEARCH_SECTION_WITH_FILTERS_HEIGHT_PX;
	}
	return SEARCH_SECTION_HEIGHT_PX;
}

function getPharmacyPickerScrollLayout(params: {
	fillRemaining: boolean;
	listHeight: number;
}): PharmacyPickerScrollLayout {
	if (params.fillRemaining) {
		return {
			className: `min-h-0 h-full flex-1 overflow-hidden bg-gray-100 ${EXPANDED_VIEWPORT_CLASSES}`,
			height: undefined,
		};
	}
	return {
		className: `min-h-0 overflow-y-auto md:overflow-y-hidden ${EXPANDED_VIEWPORT_CLASSES}`,
		height: `${params.listHeight}px`,
	};
}

type PharmacyPickerResponsiveVendorSelectProps =
	PharmacyPickerVendorSelectProps & {
		mobileMapSlot?: ReactNode;
	};

export function PharmacyPickerResponsiveVendorSelect({
	mobileMapSlot,
	onDropdownOpenChange,
	...props
}: PharmacyPickerResponsiveVendorSelectProps) {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const isDropdownOpenRef = useRef(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [, setRenderKey] = useState(0);
	const footerSlotRef = useRef(props.footerSlot);
	footerSlotRef.current = props.footerSlot;
	const onDropdownOpenChangeRef = useRef(onDropdownOpenChange);
	onDropdownOpenChangeRef.current = onDropdownOpenChange;
	const searchSectionHeightRef = useRef(SEARCH_SECTION_HEIGHT_PX);
	searchSectionHeightRef.current = getSearchSectionHeightPx(
		props.enablePharmacyFilters,
	);
	const fillNoResultsRef = useRef(false);
	fillNoResultsRef.current =
		Boolean(props.enablePharmacyFilters) &&
		props.searchQuery.trim().length > 0 &&
		props.pharmacies.length === 0;
	const WithHeight = useMemo(
		() =>
			function WithHeight({ children }: { children: ReactNode }) {
				const fillRemaining = fillNoResultsRef.current;
				const hasFooterSlot = Boolean(footerSlotRef.current) && !fillRemaining;
				const listHeight = getSelectVendorListHeight({
					viewportHeight: getViewportInnerHeight(),
					triggerBottom:
						triggerRef.current?.getBoundingClientRect().bottom ?? 0,
					searchSectionHeightPx: searchSectionHeightRef.current,
					hasFooterSlot,
					isPharmacy: true,
					isDropdownOpen: isDropdownOpenRef.current,
					isDesktop: isDesktopViewport(),
					sideOffsetPx: PharmacyPickerVendorSelect.CONTENT_SIDE_OFFSET_PX,
				});
				const layout = getPharmacyPickerScrollLayout({
					fillRemaining,
					listHeight,
				});
				return (
					<div
						ref={scrollContainerRef}
						data-testid="pharmacy-picker-scroll-container"
						className={layout.className}
						style={{ height: layout.height }}
					>
						{children}
					</div>
				);
			},
		[],
	);
	const rerender = useDebouncedCallback(() => setRenderKey((k) => k + 1), {
		leading: false,
		trailing: true,
	});
	useEffect(() => {
		window.addEventListener("resize", rerender);
		window.addEventListener("scroll", rerender, true);
		return () => {
			window.removeEventListener("resize", rerender);
			window.removeEventListener("scroll", rerender, true);
		};
	}, [rerender]);
	useEffect(() => {
		const trigger = triggerRef.current;
		if (!trigger) return;
		const emitOpenState = () => {
			const isOpen = trigger.getAttribute("aria-expanded") === "true";
			isDropdownOpenRef.current = isOpen;
			setRenderKey((k) => k + 1);
			onDropdownOpenChangeRef.current?.(isOpen);
		};
		emitOpenState();
		const observer = new MutationObserver(emitOpenState);
		observer.observe(trigger, {
			attributes: true,
			attributeFilter: ["aria-expanded"],
		});
		return () => {
			observer.disconnect();
			isDropdownOpenRef.current = false;
			onDropdownOpenChangeRef.current?.(false);
		};
	}, []);
	return (
		<PharmacyPickerVendorSelect
			{...props}
			triggerRef={triggerRef}
			contentWrapper={WithHeight}
			mobileMapSlot={mobileMapSlot}
		/>
	);
}
